<template>
  <BaseModal v-if="products.length > 0" :is-open="isOpen" size="sm">
    <template #header>
      <NavBar>
        <span slot="left">
          <BackButton @click="close" />
        </span>
        <span
          slot="center"
          class="wrn-color-black"
          data-testid="risk-level-title"
        >
          Nível de risco
        </span>
        <span slot="right">
          <CloseButton @click="close" />
        </span>
      </NavBar>
    </template>

    <template #content>
      <div class="list-product-content">
        <BaseText :color="NebraskaColors.textPrimary">
          Você escolheu um ou mais produtos que não são recomendados para seu
          nível de tolerância a risco em produtos de investimentos
        </BaseText>

        <hr class="list-products-divider wrn-m-t-05 wrn-m-b-0" />

        <div>
          <Row :has-interaction="false" :has-line="false">
            <template #top-left>
              <span class="profile-text">Seu Perfil</span>
            </template>
            <template #top-right>
              <span>{{ computedRiskToleranceLabel }}</span>
            </template>
            <template #icon-right>
              <RoundedIcon
                role="button"
                data-testid="icon-info"
                icon-name="ic_info"
                :has-hover="true"
                :text-color="NebraskaColors.elementPrimary"
                main-color="transparent"
                aria-label="Ver perfil de investidor"
                @click="openInvestorProfileModal"
              />
            </template>
          </Row>
        </div>

        <div>
          <p class="product-title">Produtos</p>

          <div v-for="(product, i) in products" :key="i" class="wrn-m-b-05">
            <ProductDetail>
              <template #title>
                <div class="row-list-product">
                  <Row :has-interaction="false" :has-line="false">
                    <template #bottom-left>
                      <span>{{ product.name }}</span>
                    </template>
                  </Row>
                </div>
              </template>

              <template #content>
                <div class="row-list-product">
                  <Row :has-line="false" :has-interaction="false">
                    <template #top-left>
                      <span class="profile-level-text">Nível de risco</span>
                    </template>
                    <template #icon-right>
                      <RiskScore :risk-level="product.riskScore" />
                    </template>
                  </Row>
                </div>
              </template>
            </ProductDetail>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <div
        class="wrn-checkbox-container wrn-c-12 wrn-color-black wrn-input-lg checkbox-section"
      >
        <Checkbox
          v-model="checkboxValue"
          class="wrn-bg-white wrn-checkbox selected-products-checkbox wrn-m-x-5"
          :class="{ 'wrn-checked': checkboxValue }"
          data-testid="terms-acceptance-checkbox"
          @input="trackCheckbox"
        >
          <BaseText :color="NebraskaColors.textSecondary">
            Estou ciente e quero investir em produtos fora do meu nível de risco
          </BaseText>
        </Checkbox>
      </div>

      <ModalFooter
        slot="footer"
        left-button-label="Cancelar"
        :right-button-label="rightButtonText"
        :disabled-right-button="!checkboxValue"
        @left-button-clicked="close"
        @right-button-clicked="emitAccepted"
      />
    </template>
  </BaseModal>
</template>
<script lang="ts">
import { Component, Emit, Prop, Vue } from 'vue-property-decorator';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import {
  NavBar,
  BaseText,
  Row,
  RoundedIcon,
  BaseModal
} from '@warrenbrasil/nebraska-web';
import { ModalFooter } from '@/modules/common/components/__deprecated__/Modal';
import { CloseButton } from '@/modules/common/components/buttons';
import { BackButton } from '@/modules/common/components/buttons/BackButton';
import { RiskScore } from '@/modules/common/components/RiskScore';
import { Checkbox } from '@/modules/common/components/__deprecated__/Checkbox';
import { ProductDetail } from '@/modules/common/components/ProductDetail';
import * as trackerRiskLevel from './track-events';

@Component({
  components: {
    BaseModal,
    ModalFooter,
    CloseButton,
    BackButton,
    NavBar,
    BaseText,
    Checkbox,
    Row,
    RoundedIcon,
    ProductDetail,
    RiskScore
  }
})
export default class RiskLevelModal extends Vue {
  @Prop({ type: Boolean, required: false, default: false })
  public isOpen!: boolean;

  @Prop({ type: String, required: false, default: 'Criar Carteira' })
  public rightButtonText!: boolean;

  @Prop({ type: Boolean, default: false })
  public isInvestmentByProduct?: boolean;

  @Prop({ type: Array, required: false })
  public products!: string[];

  @Prop({ type: String, required: false })
  public computedRiskToleranceLabel!: string;

  private NebraskaColors = NebraskaColors;
  private checkboxValue = false;

  private trackCheckbox() {
    trackerRiskLevel.trackCheckbox();
  }

  @Emit('cancelled')
  public close() {
    trackerRiskLevel.trackClose();
  }

  @Emit('accepted')
  public emitAccepted() {
    trackerRiskLevel.trackAccepted();
    if (this.isInvestmentByProduct) {
      trackerRiskLevel.trackAcceptedAmplitude();
    }
  }

  private beforeMount() {
    if (!this.products.length) {
      this.$emit('accepted');
    }
  }

  private openInvestorProfileModal() {
    this.$emit('open-investor-profile');
  }
}
</script>
<style lang="less" scoped>
.list-product-content {
  padding: calc(@size-spacing-x600 + @size-spacing-x300) @size-spacing-x800 0
    @size-spacing-x800;
}

.rounded-item span {
  align-items: flex-start !important;
}

.profile-text {
  color: @text-secondary;
  font-size: 20px;
  line-height: 18px;
}

.product-title {
  margin: @size-spacing-x400 0 @size-spacing-x350 0;
  text-align: justify;
  color: @text-secondary;
  font-size: 14px;
  line-height: 18px;
  font-weight: bold;
  text-transform: uppercase;
}

.profile-level-text {
  color: @text-secondary;
  font-size: 16px !important;
}

.list-products-divider {
  border-top: 1px solid @divider-primary;
  width: 100%;
}

.row-list-product {
  width: 100%;
}

.row-list-product {
  /deep/ .row-item > div {
    padding: 0 0 !important;
    margin: 0 0 !important;
  }
}

.checkbox-section {
  border-top: 1px solid @divider-primary;
  padding: @size-spacing-x300 @size-spacing-x800;
}

/deep/ .wrapper .icon {
  margin: 0 !important;
  padding: 0 !important;
}
</style>
